import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
    Box,
    Typography,
    Paper,
    Tabs,
    Tab,
    IconButton,
    CircularProgress,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    AppBar,
    Toolbar,
    Container,
    Grid,
    Card,
    CardContent,
    CardMedia,
    CardActions,
    Chip,
    useTheme,
    useMediaQuery
} from "@mui/material";
import {
    Visibility as VisibilityIcon,
    Close as CloseIcon,
    Download as DownloadIcon,
    FileDownload as FileDownloadIcon,
    AddToDrive as AddToDriveIcon,
    Event as EventIcon,
    CardGiftcard as GiftIcon,
    Favorite as WishIcon
} from "@mui/icons-material";
import { collection, query, orderBy, onSnapshot, doc, getDoc } from "firebase/firestore";
import { db } from "../../../firebase/config";
import Swal from "sweetalert2";
import JSZip from "jszip";
import { saveAs } from "file-saver";

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

const PublicActivityPage = () => {
    const { id: currentEventId } = useParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [tabValue, setTabValue] = useState(0);
    const [wishes, setWishes] = useState([]);
    const [gifts, setGifts] = useState([]);
    const [eventName, setEventName] = useState("");
    const [eventTheme, setEventTheme] = useState({});
    const [loading, setLoading] = useState(true);
    const [previewImage, setPreviewImage] = useState(null);
    const [imgLoading, setImgLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [driveExporting, setDriveExporting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState("");

    // Real-time listener for Wishes and Gifts
    useEffect(() => {
        if (!currentEventId) return;
        setLoading(true);

        // Fetch Event Name
        const fetchEventInfo = async () => {
            try {
                const eventDoc = await getDoc(doc(db, "events", currentEventId));
                if (eventDoc.exists()) {
                    const data = eventDoc.data();
                    console.log(data)
                    setEventName(data.information?.eventName || "");
                    setEventTheme(data.theme || {});
                }
            } catch (e) {
                console.error("Error fetching event info", e);
            }
        };
        fetchEventInfo();

        const wishesRef = collection(db, "events", currentEventId, "wishes");
        const wishesQuery = query(wishesRef, orderBy("createdAt", "desc"));

        const unsubscribeWishes = onSnapshot(wishesQuery, (snapshot) => {
            const wishesList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : new Date(doc.data().createdAt)
            }));
            setWishes(wishesList);
        }, (error) => {
            console.error("Error listening to wishes:", error);
        });

        const giftsRef = collection(db, "events", currentEventId, "gifts");
        const giftsQuery = query(giftsRef, orderBy("createdAt", "desc"));

        const unsubscribeGifts = onSnapshot(giftsQuery, (snapshot) => {
            const giftsList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : new Date(doc.data().createdAt)
            }));
            setGifts(giftsList);
            setLoading(false); // Assume initial load done when both start listening
        }, (error) => {
            console.error("Error listening to gifts:", error);
            setLoading(false);
        });

        return () => {
            unsubscribeWishes();
            unsubscribeGifts();
        };
    }, [currentEventId]);

    // Update document title
    useEffect(() => {
        if (eventName) document.title = eventName;
    }, [eventName]);

    // Derived Colors
    const primaryColor = eventTheme.mainColor?.value || "#8A5918";
    const secondaryColor = eventTheme.secondaryColor?.value || "#FFF3E0";
    // const fontColor = eventTheme.fontColor?.value || "#7A3E00";

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleDownload = async (url) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = `image_${Date.now()}.jpg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Download failed:", error);
            window.open(url, "_blank");
        }
    };

    // ... (Keep createDriveFolder, uploadDriveFile, generateZipBlob, handleExport, handleDriveExport logic same as before)
    // To save space in this prompt, I will assume the export logic is identical to previous version.
    // I will copy it back in full implementation.

    const createDriveFolder = async (name, parentId, accessToken) => {
        const metadata = {
            name,
            mimeType: 'application/vnd.google-apps.folder',
            parents: parentId ? [parentId] : []
        };

        const response = await fetch('https://www.googleapis.com/drive/v3/files?fields=id,webViewLink', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(metadata)
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error?.message || 'Failed to create folder');
        }
        return await response.json();
    };

    const uploadDriveFile = async (blob, name, mimeType, parentId, accessToken) => {
        const metadata = {
            name,
            mimeType,
            parents: parentId ? [parentId] : []
        };

        const form = new FormData();
        form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
        form.append('file', blob);

        const response = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,webViewLink', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
            },
            body: form,
        });

        if (!response.ok) throw new Error('Failed to upload file');
        return await response.json();
    };

    const generateZipBlob = async () => {
        const zip = new JSZip();

        // Generate Wishes CSV
        const wishesHeader = ["Date", "Sender", "Message", "Image URL", "Image Filename"];
        const wishesRows = wishes.map(w => {
            const url = w.imageUrl || w.image;
            const filename = url ? `wish_${w.id}.jpg` : "";
            return [
                w.createdAt ? new Date(w.createdAt).toLocaleString() : "",
                `"${(w.sender || w.name || "Anonymous").replace(/"/g, '""')}"`,
                `"${(w.message || w.wish || "").replace(/"/g, '""')}"`,
                url || "",
                filename
            ];
        });
        const wishesCsv = [wishesHeader.join(","), ...wishesRows.map(r => r.join(","))].join("\n");
        zip.file("wishes.csv", wishesCsv);

        // Fetch and add Wish images
        const wishImgFolder = zip.folder("wishes_images");
        for (const w of wishes) {
            const url = w.imageUrl || w.image;
            if (url) {
                try {
                    const response = await fetch(url);
                    const blob = await response.blob();
                    wishImgFolder.file(`wish_${w.id}.jpg`, blob);
                } catch (e) {
                    console.error("Failed to fetch wish image", e);
                }
            }
        }

        // Generate Gifts CSV
        const giftsHeader = ["Date", "Sender", "Slip URL", "Slip Filename"];
        const giftsRows = gifts.map(g => {
            const filename = g.slipUrl ? `gift_${g.id}.jpg` : "";
            return [
                g.createdAt ? new Date(g.createdAt).toLocaleString() : "",
                `"${(g.sender || g.name || "Anonymous").replace(/"/g, '""')}"`,
                g.slipUrl || "",
                filename
            ];
        });
        const giftsCsv = [giftsHeader.join(","), ...giftsRows.map(r => r.join(","))].join("\n");
        zip.file("gifts.csv", giftsCsv);

        // Fetch and add Gift images
        const giftImgFolder = zip.folder("gifts_images");
        for (const g of gifts) {
            if (g.slipUrl) {
                try {
                    const response = await fetch(g.slipUrl);
                    const blob = await response.blob();
                    giftImgFolder.file(`gift_${g.id}.jpg`, blob);
                } catch (e) {
                    console.error("Failed to fetch gift image", e);
                }
            }
        }

        return await zip.generateAsync({ type: "blob" });
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const content = await generateZipBlob();
            saveAs(content, `event_data_${currentEventId}.zip`);
        } catch (error) {
            console.error("Export failed", error);
            Swal.fire("Error", "Failed to export data", "error");
        } finally {
            setExporting(false);
        }
    };

    const handleDriveExport = async () => {
        const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
        if (!clientId) {
            Swal.fire({
                title: "Configuration Error",
                text: "Google Client ID is missing. Please add REACT_APP_GOOGLE_CLIENT_ID to your .env file.",
                icon: "error"
            });
            return;
        }

        setDriveExporting(true);
        setUploadProgress("Initializing...");

        try {
            if (!window.google) {
                await new Promise((resolve, reject) => {
                    const script = document.createElement("script");
                    script.src = "https://accounts.google.com/gsi/client";
                    script.onload = resolve;
                    script.onerror = reject;
                    document.body.appendChild(script);
                });
            }

            const tokenClient = window.google.accounts.oauth2.initTokenClient({
                client_id: clientId,
                scope: "https://www.googleapis.com/auth/drive.file",
                callback: async (tokenResponse) => {
                    if (tokenResponse.error) {
                        console.error("Token Error:", tokenResponse);
                        setDriveExporting(false);
                        setUploadProgress("");
                        return;
                    }

                    if (!window.google.accounts.oauth2.hasGrantedAllScopes(tokenResponse, 'https://www.googleapis.com/auth/drive.file')) {
                        Swal.fire("Error", "Permission to access Drive was denied. Please grant permission to save the file.", "error");
                        setDriveExporting(false);
                        setUploadProgress("");
                        return;
                    }

                    if (tokenResponse && tokenResponse.access_token) {
                        try {
                            const accessToken = tokenResponse.access_token;
                            setUploadProgress("Creating folder...");
                            const folderName = `Event Data - ${new Date().toLocaleDateString()} - ${currentEventId}`;
                            const mainFolder = await createDriveFolder(folderName, null, accessToken);

                            setUploadProgress("Uploading CSVs...");
                            const wishesHeader = ["Date", "Sender", "Message", "Image URL", "Image Filename"];
                            const wishesRows = wishes.map(w => {
                                const url = w.imageUrl || w.image;
                                const filename = url ? `wish_${w.id}.jpg` : "";
                                return [
                                    w.createdAt ? new Date(w.createdAt).toLocaleString() : "",
                                    `"${(w.sender || w.name || "Anonymous").replace(/"/g, '""')}"`,
                                    `"${(w.message || w.wish || "").replace(/"/g, '""')}"`,
                                    url || "",
                                    filename
                                ];
                            });
                            const wishesCsv = [wishesHeader.join(","), ...wishesRows.map(r => r.join(","))].join("\n");
                            const wishesBlob = new Blob([wishesCsv], { type: 'text/csv' });
                            await uploadDriveFile(wishesBlob, 'wishes.csv', 'text/csv', mainFolder.id, accessToken);

                            const giftsHeader = ["Date", "Sender", "Slip URL", "Slip Filename"];
                            const giftsRows = gifts.map(g => {
                                const filename = g.slipUrl ? `gift_${g.id}.jpg` : "";
                                return [
                                    g.createdAt ? new Date(g.createdAt).toLocaleString() : "",
                                    `"${(g.sender || g.name || "Anonymous").replace(/"/g, '""')}"`,
                                    g.slipUrl || "",
                                    filename
                                ];
                            });
                            const giftsCsv = [giftsHeader.join(","), ...giftsRows.map(r => r.join(","))].join("\n");
                            const giftsBlob = new Blob([giftsCsv], { type: 'text/csv' });
                            await uploadDriveFile(giftsBlob, 'gifts.csv', 'text/csv', mainFolder.id, accessToken);

                            const wishesWithImages = wishes.filter(w => w.imageUrl || w.image);
                            if (wishesWithImages.length > 0) {
                                const wishesFolder = await createDriveFolder("Wishes Images", mainFolder.id, accessToken);
                                let count = 0;
                                for (const w of wishesWithImages) {
                                    const url = w.imageUrl || w.image;
                                    if (url) {
                                        try {
                                            count++;
                                            setUploadProgress(`Uploading Wish Image ${count}/${wishesWithImages.length}`);
                                            const imgResp = await fetch(url);
                                            const imgBlob = await imgResp.blob();
                                            await uploadDriveFile(imgBlob, `wish_${w.id}.jpg`, 'image/jpeg', wishesFolder.id, accessToken);
                                        } catch (e) {
                                            console.error("Failed to upload wish image", e);
                                        }
                                    }
                                }
                            }

                            const giftsWithSlips = gifts.filter(g => g.slipUrl);
                            if (giftsWithSlips.length > 0) {
                                const giftsFolder = await createDriveFolder("Gifts Images", mainFolder.id, accessToken);
                                let count = 0;
                                for (const g of giftsWithSlips) {
                                    if (g.slipUrl) {
                                        try {
                                            count++;
                                            setUploadProgress(`Uploading Gift Slip ${count}/${giftsWithSlips.length}`);
                                            const imgResp = await fetch(g.slipUrl);
                                            const imgBlob = await imgResp.blob();
                                            await uploadDriveFile(imgBlob, `gift_${g.id}.jpg`, 'image/jpeg', giftsFolder.id, accessToken);
                                        } catch (e) {
                                            console.error("Failed to upload gift slip", e);
                                        }
                                    }
                                }
                            }

                            Swal.fire({
                                title: "Success",
                                text: "All files uploaded to Google Drive!",
                                icon: "success",
                                footer: `<a href="${mainFolder.webViewLink}" target="_blank">Open Folder in Drive</a>`
                            });

                        } catch (error) {
                            console.error("Drive upload failed", error);
                            Swal.fire("Error", `Failed to upload to Google Drive: ${error.message}`, "error");
                        } finally {
                            setDriveExporting(false);
                            setUploadProgress("");
                        }
                    } else {
                        setDriveExporting(false);
                        setUploadProgress("");
                    }
                },
            });

            tokenClient.requestAccessToken();
        } catch (error) {
            console.error("Drive init failed", error);
            Swal.fire("Error", "Failed to initialize Google Sign-In", "error");
            setDriveExporting(false);
            setUploadProgress("");
        }
    };

    const formatDate = (date) => {
        if (!date) return "-";
        return new Date(date).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Box sx={{ width: "100%", minHeight: "100vh", bgcolor: "#F8F9FA" }}>
            {/* Header */}
            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    bgcolor: "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(20px)",
                    borderBottom: "1px solid",
                    borderColor: "divider"
                }}
            >
                <Toolbar sx={{ py: 2 }}>
                    <Container maxWidth="xl" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                            <Box
                                sx={{
                                    width: 48,
                                    height: 48,
                                    bgcolor: '#8A5918',
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 4px 12px rgba(138, 89, 24, 0.2)'
                                }}
                            >
                                <EventIcon sx={{ fontSize: 28, color: 'white' }} />
                            </Box>
                            <Box>
                                <Typography variant="h5" component="div" sx={{ fontWeight: 800, color: '#1A1A1A', letterSpacing: '-0.5px' }}>
                                    {eventName}
                                </Typography>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#4CAF50' }} />
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                        Live Updates
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <Button
                                variant="outlined"
                                sx={{
                                    borderRadius: '50px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderColor: '#E0E0E0',
                                    color: '#555',
                                    px: 3,
                                    '&:hover': { borderColor: '#BDBDBD', bgcolor: '#F5F5F5' }
                                }}
                                startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
                                onClick={handleExport}
                                disabled={exporting || driveExporting || (wishes.length === 0 && gifts.length === 0)}
                            >
                                {exporting ? "Exporting..." : "Download ZIP"}
                            </Button>
                            <Button
                                variant="contained"
                                sx={{
                                    borderRadius: '50px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    px: 3,
                                    bgcolor: '#8A5918',
                                    boxShadow: '0 4px 12px rgba(138, 89, 24, 0.3)',
                                    '&:hover': { bgcolor: '#6b3b07', boxShadow: '0 6px 16px rgba(138, 89, 24, 0.4)' }
                                }}
                                startIcon={driveExporting ? <CircularProgress size={16} color="inherit" /> : <AddToDriveIcon />}
                                onClick={handleDriveExport}
                                disabled={exporting || driveExporting || (wishes.length === 0 && gifts.length === 0)}
                            >
                                {driveExporting ? (uploadProgress || "Uploading...") : "Save to Drive"}
                            </Button>
                        </Box>
                    </Container>
                </Toolbar>
            </AppBar>

            <Container maxWidth="xl" sx={{ py: 4, px: { xs: 1, md: 3 } }}>
                {/* Tabs */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 0.5,
                            bgcolor: 'white',
                            borderRadius: '50px',
                            border: '1px solid',
                            borderColor: 'divider',
                            display: 'inline-flex'
                        }}
                    >
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            sx={{
                                minHeight: 48,
                                '& .MuiTabs-indicator': { display: 'none' }
                            }}
                        >
                            <Tab
                                icon={<WishIcon sx={{ fontSize: 20 }} />}
                                iconPosition="start"
                                label={`Wishes (${wishes.length})`}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    borderRadius: '50px',
                                    minHeight: 48,
                                    px: { xs: 2, md: 4 },
                                    color: 'text.secondary',
                                    '&.Mui-selected': {
                                        color: '#8A5918',
                                        bgcolor: 'rgba(138, 89, 24, 0.08)'
                                    }
                                }}
                            />
                            <Tab
                                icon={<GiftIcon sx={{ fontSize: 20 }} />}
                                iconPosition="start"
                                label={`Gifts (${gifts.length})`}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    borderRadius: '50px',
                                    minHeight: 48,
                                    px: { xs: 2, md: 4 },
                                    color: 'text.secondary',
                                    '&.Mui-selected': {
                                        color: '#8A5918',
                                        bgcolor: 'rgba(138, 89, 24, 0.08)'
                                    }
                                }}
                            />
                        </Tabs>
                    </Paper>
                </Box>

                {loading ? (
                    <Box display="flex" justifyContent="center" p={10}>
                        <CircularProgress sx={{ color: primaryColor }} />
                    </Box>
                ) : (
                    <>
                        <TabPanel value={tabValue} index={0}>
                            {wishes.length > 0 ? (
                                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 3, width: "100%" }}>
                                    {wishes.map((wish) => {
                                        const wishImg = wish.imageUrl || wish.image || "https://placehold.co/400x400/FFFFFF/8A5918?text=Preview";
                                        const hasRealImage = !!(wish.imageUrl || wish.image);

                                        return (
                                            <Box key={wish.id} sx={{ m: 2 }}>
                                                <Card
                                                    elevation={0}
                                                    sx={{
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        borderRadius: 3,
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        transition: 'all 0.3s ease',
                                                        overflow: 'hidden',
                                                        width: "100%",
                                                        '&:hover': {
                                                            transform: 'translateY(-8px)',
                                                            boxShadow: '0 12px 30px rgba(0,0,0,0.08)',
                                                            borderColor: 'transparent'
                                                        }
                                                    }}
                                                >
                                                    <CardImage
                                                        src={wishImg}
                                                        alt="Wish Image"
                                                        color={primaryColor}
                                                        onClick={() => {
                                                            setPreviewImage(wishImg);
                                                            setImgLoading(true);
                                                        }}
                                                    />
                                                    <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                                                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                                                            <Box
                                                                sx={{
                                                                    width: 32,
                                                                    height: 32,
                                                                    borderRadius: '50%',
                                                                    bgcolor: secondaryColor,
                                                                    color: primaryColor,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    fontWeight: 'bold',
                                                                    fontSize: '14px'
                                                                }}
                                                            >
                                                                {(wish.sender || wish.name || "A").charAt(0).toUpperCase()}
                                                            </Box>
                                                            <Typography variant="subtitle1" fontWeight={700} noWrap>
                                                                {wish.sender || wish.name || "Anonymous"}
                                                            </Typography>
                                                        </Box>

                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{
                                                                mb: 2,
                                                                whiteSpace: 'pre-wrap',
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 3,
                                                                WebkitBoxOrient: 'vertical',
                                                                overflow: 'hidden',
                                                                textAlign: 'left'
                                                            }}
                                                        >
                                                            {wish.message || wish.wish || "No message"}
                                                        </Typography>

                                                        <Box display="flex" justifyContent="space-between" alignItems="center" mt="auto">
                                                            <Typography variant="caption" color="text.disabled" fontWeight={500}>
                                                                {formatDate(wish.createdAt)}
                                                            </Typography>
                                                        </Box>
                                                    </CardContent>
                                                    <Box sx={{ p: 2, pt: 0 }}>
                                                        <Button
                                                            fullWidth
                                                            variant="outlined"
                                                            size="small"
                                                            startIcon={<VisibilityIcon />}
                                                            onClick={() => {
                                                                setPreviewImage(wishImg);
                                                                setImgLoading(true);
                                                            }}
                                                            sx={{
                                                                borderRadius: '8px',
                                                                textTransform: 'none',
                                                                borderColor: 'divider',
                                                                color: 'text.primary',
                                                                '&:hover': { borderColor: '#8A5918', color: '#8A5918', bgcolor: 'transparent' }
                                                            }}
                                                        >
                                                            View Image
                                                        </Button>
                                                    </Box>
                                                </Card>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            ) : (
                                <Box width="100%" textAlign="center" py={10}>
                                    <WishIcon sx={{ fontSize: 60, color: '#E0E0E0', mb: 2 }} />
                                    <Typography color="text.secondary" fontWeight={500}>No wishes yet.</Typography>
                                </Box>
                            )}
                        </TabPanel>

                        <TabPanel value={tabValue} index={1}>
                            {gifts.length > 0 ? (
                                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 3, width: "100%" }}>
                                    {gifts.map((gift) => {
                                        const giftImg = gift.slipUrl || "https://placehold.co/400x400/FFFFFF/8A5918?text=Preview";

                                        return (
                                            <Box key={gift.id} sx={{ m: 2 }}>
                                                <Card
                                                    elevation={0}
                                                    sx={{
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        borderRadius: 3,
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        transition: 'all 0.3s ease',
                                                        overflow: 'hidden',
                                                        width: "100%",
                                                        '&:hover': {
                                                            transform: 'translateY(-8px)',
                                                            boxShadow: '0 12px 30px rgba(0,0,0,0.08)',
                                                            borderColor: 'transparent'
                                                        }
                                                    }}
                                                >
                                                    <CardImage
                                                        src={giftImg}
                                                        alt="Gift Slip"
                                                        color={primaryColor}
                                                        onClick={() => {
                                                            setPreviewImage(giftImg);
                                                            setImgLoading(true);
                                                        }}
                                                    />

                                                    <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                                                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                                                            <Box
                                                                sx={{
                                                                    width: 32,
                                                                    height: 32,
                                                                    borderRadius: '50%',
                                                                    bgcolor: secondaryColor, // Changed from '#E3F2FD'
                                                                    color: primaryColor, // Changed from '#1976D2'
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    fontWeight: 'bold',
                                                                    fontSize: '14px'
                                                                }}
                                                            >
                                                                {(gift.sender || gift.name || "A").charAt(0).toUpperCase()}
                                                            </Box>
                                                            <Typography variant="subtitle1" fontWeight={700} noWrap>
                                                                {gift.sender || gift.name || "Anonymous"}
                                                            </Typography>
                                                        </Box>

                                                        <Box display="flex" justifyContent="space-between" alignItems="center" mt="auto">
                                                            <Typography variant="caption" color="text.disabled" fontWeight={500}>
                                                                {formatDate(gift.createdAt)}
                                                            </Typography>
                                                        </Box>
                                                    </CardContent>

                                                    <Box sx={{ p: 2, pt: 0 }}>
                                                        <Button
                                                            fullWidth
                                                            variant="outlined"
                                                            size="small"
                                                            startIcon={<VisibilityIcon />}
                                                            onClick={() => {
                                                                setPreviewImage(giftImg);
                                                                setImgLoading(true);
                                                            }}
                                                            sx={{
                                                                borderRadius: '8px',
                                                                textTransform: 'none',
                                                                borderColor: 'divider',
                                                                color: 'text.primary',
                                                                '&:hover': { borderColor: primaryColor, color: primaryColor, bgcolor: 'transparent' }
                                                            }}
                                                        >
                                                            View Slip
                                                        </Button>
                                                    </Box>
                                                </Card>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            ) : (
                                <Box width="100%" textAlign="center" py={10}>
                                    <GiftIcon sx={{ fontSize: 60, color: '#E0E0E0', mb: 2 }} />
                                    <Typography color="text.secondary" fontWeight={500}>No gifts yet.</Typography>
                                </Box>
                            )}
                        </TabPanel>
                    </>
                )}
            </Container>

            <Dialog
                open={!!previewImage}
                onClose={() => setPreviewImage(null)}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: { borderRadius: isMobile ? 0 : 3, overflow: 'hidden' }
                }}
                BackdropProps={{
                    sx: { backdropFilter: 'blur(5px)' }
                }}
            >
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'white' }}>
                    <Typography variant="h6" fontWeight={700}>Preview</Typography>
                    <IconButton
                        aria-label="close"
                        onClick={() => setPreviewImage(null)}
                        sx={{ color: (theme) => theme.palette.grey[500] }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflowY: 'auto' }}>
                    <Box display="flex" justifyContent="center" alignItems="center" width="100%" minHeight="400px">
                        {imgLoading && <CircularProgress sx={{ color: primaryColor }} />}
                        <img
                            src={previewImage}
                            alt="Preview"
                            style={{
                                width: "100%",
                                height: "auto",
                                display: imgLoading ? "none" : "block"
                            }}
                            onLoad={() => setImgLoading(false)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: 'white' }}>
                    <Button onClick={() => setPreviewImage(null)} sx={{ color: 'text.secondary' }}>Close</Button>
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownload(previewImage)}
                        sx={{
                            bgcolor: primaryColor,
                            borderRadius: '50px',
                            px: 3,
                            '&:hover': { bgcolor: primaryColor, opacity: 0.9 }
                        }}
                    >
                        Download
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const CardImage = ({ src, alt, onClick, color = '#8A5918' }) => {
    const [loading, setLoading] = useState(true);

    return (
        <Box sx={{ position: 'relative', height: 200, overflow: 'hidden', width: '100%', bgcolor: '#f0f0f0' }}>
            {loading && (
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
                    <CircularProgress size={30} sx={{ color: color }} />
                </Box>
            )}
            <CardMedia
                component="img"
                image={src}
                alt={alt}
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    cursor: 'pointer',
                    transition: 'transform 0.5s ease, opacity 0.3s ease',
                    opacity: loading ? 0 : 1,
                    '&:hover': { transform: 'scale(1.05)' }
                }}
                onClick={onClick}
                onLoad={() => setLoading(false)}
                onError={(e) => {
                    e.target.src = "https://placehold.co/400x400/FFFFFF/8A5918?text=Preview";
                    setLoading(false);
                }}
            />
        </Box>
    );
};

export default PublicActivityPage;
